// Inspired by react-hot-toast library
import * as React from "react";

type PermissionDialog = {
  permissionName?: string;
};

const actionTypes = {
  SHOW_DIALOG: "SHOW_DIALOG",
  HIDE_DIALOG: "HIDE_DIALOG",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["SHOW_DIALOG"];
      permission: PermissionDialog;
    }
  | {
      type: ActionType["HIDE_DIALOG"];
      permission: Partial<PermissionDialog>;
    };

interface State {
  permitted: boolean;
  permissionName: string;
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SHOW_DIALOG":
      return {
        ...state,
        permitted: false,
        permissionName: action.permission.permissionName,
      };

    case "HIDE_DIALOG":
      return {
        ...state,
        permitted: true,
        permissionName: "",
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { permitted: true, permissionName: "" };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Permission = PermissionDialog;

function notPermitted({ ...props }: Permission = {}) {
  dispatch({
    type: "SHOW_DIALOG",
    permission: { ...props },
  });
}

function closeDialog() {
  dispatch({
    type: "HIDE_DIALOG",
    permission: { permissionName: "" },
  });
}

function usePermission() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    notPermitted,
    closeDialog,
  };
}

export { usePermission };
